export type VerificationResult = "unknown" | "success" | "failed" | "skipped";

export interface GatewayTokenStatus {
  readonly configured: boolean;
  readonly maskedSuffix: string | null;
  readonly fingerprint: string | null;
  readonly lastVerifiedAt: string | null;
  readonly lastVerificationResult: VerificationResult;
}

export type HostedReadinessFailureReason =
  | "token_verification_failed"
  | "bridge_connectivity_unavailable";

export interface HostedReadiness {
  readonly generalApiReachable: boolean;
  readonly telematikAvailable: boolean;
  readonly selectedCardReaderAvailable: boolean;
  readonly hasMedicalCard: boolean;
  readonly failureReason: HostedReadinessFailureReason | null;
}

export interface GatewaySession {
  readonly enrolled: boolean;
  readonly ready: boolean;
  readonly revoked: boolean;
  readonly expired: boolean;
  readonly deviceId: string | null;
  readonly practiceName: string | null;
  readonly deviceName: string | null;
  readonly insuiteRestUrl: string | null;
  readonly token: GatewayTokenStatus;
  readonly validationIssues: readonly string[];
}

export interface EnrollmentRequest {
  readonly insuiteRestUrl: string;
  readonly solToken: string;
  readonly practiceName: string;
  readonly deviceName: string;
}

export interface DemoSession {
  readonly mode: "demo";
  readonly scenarioId: string;
  readonly terminalId: string;
  readonly lastResetAt: string;
  readonly updatedAt: string;
  readonly operatorUnlocked: boolean;
}

const gatewayErrorCopy: Record<string, string> = {
  bridge_connectivity_unavailable:
    "Die Bridge meldet derzeit keine Verbindung zum inSuite-API.",
  token_verification_failed:
    "Der Server-Token konnte nicht verifiziert werden.",
  invalid_insuite_url: "Die Bridge-Adresse ist ungültig.",
  missing_soltoken: "Bitte einen Server-Token eingeben.",
  missing_server_token: "Bitte einen Server-Token eingeben.",
};

/**
 * Reads the same-origin gateway readiness/session state.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const session = await getGatewaySession()
 * ```
 */
export async function getGatewaySession(): Promise<GatewaySession> {
  return requestJson<GatewaySession>("/api/frontdesk/session", {
    method: "GET",
  });
}

/**
 * Reads the dedicated hosted-web readiness endpoint from the same-origin gateway.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const readiness = await getHostedReadiness("reader-1")
 * ```
 */
export async function getHostedReadiness(
  terminalId?: string | null,
): Promise<HostedReadiness> {
  // TEMP(web-only exhibition workaround): remove this hosted-web endpoint
  // client after decouple-frontdesk-verification-from-ti-status lands.
  const url = new URL("/api/frontdesk/readiness", window.location.origin);
  if (terminalId?.trim()) {
    url.searchParams.set("terminalId", terminalId.trim());
  }

  return requestJson<HostedReadiness>(url.pathname + url.search, {
    method: "GET",
  });
}

export async function ensureDemoSession(): Promise<DemoSession> {
  return requestJson<DemoSession>("/api/frontdesk/demo/session", {
    method: "GET",
  });
}

export async function unlockDemoOperator(pin: string): Promise<DemoSession> {
  return requestJson<DemoSession>("/api/frontdesk/demo/operator/unlock", {
    method: "POST",
    body: JSON.stringify({ pin }),
  });
}

export async function resetDemoSession(): Promise<DemoSession> {
  return requestJson<DemoSession>("/api/frontdesk/demo/operator/reset", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/**
 * Enrolls this browser as a gateway-backed FrontDesk device.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * await enrollDevice({ insuiteRestUrl, solToken, practiceName, deviceName })
 * ```
 */
export async function enrollDevice(
  input: EnrollmentRequest,
): Promise<GatewaySession> {
  return requestJson<GatewaySession>("/api/frontdesk/enroll-device", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Replaces the enrolled server-side SOLTOKEN.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * await replaceToken("new-token")
 * ```
 */
export async function replaceToken(solToken: string): Promise<GatewaySession> {
  return requestJson<GatewaySession>("/api/frontdesk/token/replace", {
    method: "POST",
    body: JSON.stringify({ solToken }),
  });
}

/**
 * Tests the enrolled server-side SOLTOKEN.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * await testToken()
 * ```
 */
export async function testToken(): Promise<GatewaySession> {
  return requestJson<GatewaySession>("/api/frontdesk/token/test", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/**
 * Revokes the current device session.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * await revokeDevice()
 * ```
 */
export async function revokeDevice(): Promise<void> {
  await requestJson<Record<string, unknown>>("/api/frontdesk/revoke-device", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

async function requestJson<T>(
  path: string,
  init: RequestInit,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "include",
    cache: "no-store",
  });

  const payload = (await response.json().catch(() => ({}))) as unknown;

  if (!response.ok) {
    throw new Error(readErrorMessage(payload, response.status));
  }

  return payload as T;
}

function readErrorMessage(payload: unknown, status: number): string {
  if (isRecord(payload)) {
    const message = payload["message"];
    if (typeof message === "string" && message.trim()) {
      return message;
    }

    const error = payload["error"];
    if (typeof error === "string") {
      return describeGatewayError(error);
    }

    const fallback = payload["error"];
    if (typeof fallback === "string") {
      return fallback;
    }
  }
  return `Gateway request failed (${status})`;
}

export function describeGatewayError(error: string): string {
  const message = gatewayErrorCopy[error];
  if (message) {
    return message;
  }

  if (error.includes("_")) {
    return error.replaceAll("_", " ");
  }

  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
